import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { BsClock } from "react-icons/bs";
import { VscListFlat } from "react-icons/vsc";
import { GiStarShuriken } from "react-icons/gi";
import { MdQuiz } from "react-icons/md";
import { TiGroupOutline } from "react-icons/ti";
import { HiOutlineLockClosed } from "react-icons/hi2";
import authService from "../../../utils/authService";
import { checkAuthAndRedirect } from "../../../utils/auth";
import {
  fetchCoursesByTrack,
  fetchSubjectsOfCourses,
  type CourseSubjectOption,
} from "../../../utils/courseUtils";
import { fetchBatchesByCourse } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import {
  TEST_TYPES,
  isFinalLikeTestType,
  isImmediateAutoReleaseTestType,
  isSlotReleaseTestType,
} from "../../../constants/testConstants";
import StudentReport from "./StudentReport";
import SecurityLogsPanel from "./SecurityLogsPanel";
import { TEST_FLOW_API } from "./testApiPaths";
import TestFilterSelect from "./TestFilterSelect";
import TestCardSchedule from "./TestCardSchedule";
import type {
  ReportData,
  ReportStudentRow,
  TestReportViewData,
} from "./testTypes";
import {
  applyExamControl,
  computeReportDetailStats,
  downloadTestReportCsv,
  fetchReportsList,
  fetchTestReportDetail,
  filterReportDetailRows,
  releaseTestReports,
  sortReportDetailRows,
  getReportFinalScorePercent,
  formatObtainedValue,
  formatFinalScoreValue,
  isReportStudentAssigned,
  getReportStudentStatusStyle,
  resolveReportStudentDisplayStatus,
  type ReportStudentSortField,
  formatReportTimeRange,
  getReportCardDate,
  getVisibleReportSearchText,
  matchesVisibleCardSearch,
} from "./testUtils";
import { useTestCardSearchQuery } from "./TestPageShell";
import { decodeTestNameFromPath, useTestRoutes } from "./testRoutes";

const metaIconStyle: CSSProperties = {
  display: "inline-block",
  fontSize: "1.2em",
  lineHeight: 1,
  verticalAlign: "middle",
  flexShrink: 0,
};

const studentAvatarStyle: CSSProperties = {
  width: "1.75rem",
  height: "1.75rem",
  borderRadius: "999px",
  backgroundColor: "#dbeafe",
  color: "#1d4ed8",
  fontSize: "0.7rem",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: "0.5rem",
  flexShrink: 0,
};

const getTestStatusBadgeStyle = (
  variant: "aborted" | "paused" | "live" | "scheduled" | "completed"
): CSSProperties => {
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.35rem 0.75rem",
    borderRadius: "999px",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1,
  };

  if (variant === "aborted") {
    return { ...base, backgroundColor: "#fee2e2", color: "#991b1b" };
  }
  if (variant === "paused") {
    return { ...base, backgroundColor: "#fef3c7", color: "#92400e" };
  }
  if (variant === "scheduled") {
    return { ...base, backgroundColor: "#dbeafe", color: "#1d4ed8" };
  }
  if (variant === "live") {
    return { ...base, backgroundColor: "#dcfce7", color: "#166534" };
  }
  return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
};

const loadingOverlayStyle: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: "rgba(255, 255, 255, 0.8)",
  zIndex: 1000,
};

type ReportSlotOption = {
  slot_id: number;
  label: string;
  status: string;
  auto_release_test_report: boolean;
  report_released: boolean;
  consider_attendance: boolean;
  can_manual_release: boolean;
  manual_release_block_reason: string | null;
  manual_release_allow_reason: string | null;
  incomplete_student_count: number;
};

const mapReportSlot = (raw: unknown): ReportSlotOption => {
  const slot = raw as {
    slot_id?: number;
    label?: string;
    status?: string;
    auto_release_test_report?: boolean;
    report_released?: boolean;
    consider_attendance?: boolean;
    can_manual_release?: boolean;
    manual_release_block_reason?: string | null;
    manual_release_allow_reason?: string | null;
    incomplete_student_count?: number;
  };
  return {
    slot_id: Number(slot.slot_id),
    label: String(slot.label || ""),
    status: String(slot.status || ""),
    auto_release_test_report: Boolean(slot.auto_release_test_report),
    report_released: Boolean(slot.report_released),
    consider_attendance: Boolean(slot.consider_attendance),
    can_manual_release: Boolean(slot.can_manual_release),
    manual_release_block_reason: slot.manual_release_block_reason ?? null,
    manual_release_allow_reason: slot.manual_release_allow_reason ?? null,
    incomplete_student_count: Number(slot.incomplete_student_count || 0),
  };
};

type ReportStudentTableProps = {
  rows: ReportStudentRow[];
  containerStyle?: CSSProperties;
  onViewStudent?: (studentId: string, studentName: string) => void;
  manualReportRelease?: boolean;
  reportReleased?: boolean;
};

type ReportStudentColumn = {
  id: ReportStudentSortField | "slNo";
  label: string;
  sortable: boolean;
  align?: "left" | "center" | "right";
};

const headerCellSx = {
  fontWeight: 600,
  fontSize: "0.8rem",
  color: "text.secondary",
  bgcolor: "#f8fafc",
  borderBottom: "1px solid",
  borderColor: "divider",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const bodyCellSx = {
  fontSize: "0.875rem",
  borderBottom: "1px solid",
  borderColor: "divider",
};

const formatCellValue = (value: number | null | undefined) =>
  value !== null && value !== undefined ? value : "--";

const getStudentInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
};

const stopRowClick = (event: React.MouseEvent) => {
  event.stopPropagation();
};

const ReportStudentTable: React.FC<ReportStudentTableProps> = ({
  rows,
  containerStyle,
  onViewStudent,
  manualReportRelease = false,
  reportReleased = false,
}) => {
  const [sortField, setSortField] = useState<ReportStudentSortField | null>("Obtained_marks");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const columns: ReportStudentColumn[] = [
    { id: "slNo", label: "Sl.No", sortable: false },
    { id: "ID", label: "ID", sortable: true },
    { id: "USN", label: "USN", sortable: true },
    { id: "Student", label: "Student", sortable: true },
    { id: "College", label: "College", sortable: true },
    { id: "Branch", label: "Branch", sortable: true },
    { id: "Category", label: "Category", sortable: true },
    { id: "display_status", label: "Status", sortable: true, align: "center" },
    { id: "Max_marks", label: "Max Marks", sortable: true, align: "center" },
    { id: "Obtained_marks", label: "Obtained", sortable: true, align: "center" },
    { id: "final_percent", label: "Final Score", sortable: true, align: "center" },
    {
      id: "violation_count",
      label: "No of violations",
      sortable: true,
      align: "center",
    },
  ];

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return sortReportDetailRows(rows, sortField, sortDirection);
  }, [rows, sortField, sortDirection]);

  const handleSort = (field: ReportStudentSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const totalColumns = columns.length + 1;

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: "transparent",
          overflowY: "auto",
          ...containerStyle,
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align ?? "left"}
                  sx={headerCellSx}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortField === column.id}
                      direction={sortField === column.id ? sortDirection : "asc"}
                      onClick={() => handleSort(column.id as ReportStudentSortField)}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          opacity: sortField === column.id ? 1 : 0.3,
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <Box component="span">{column.label}</Box>
                  )}
                </TableCell>
              ))}
              <TableCell sx={headerCellSx} align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No students match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((student, index) => (
                  <TableRow
                    key={`${student.ID}-${index}`}
                    sx={{
                      bgcolor: index % 2 === 0 ? "background.paper" : "grey.50",
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={bodyCellSx}>{index + 1}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>{student.ID}</TableCell>
                    <TableCell sx={bodyCellSx}>{student.USN || "--"}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>
                      <span className="d-inline-flex align-items-center">
                        <span style={studentAvatarStyle}>
                          {getStudentInitials(student.Student)}
                        </span>
                        {student.Student}
                      </span>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{student.College}</TableCell>
                    <TableCell sx={bodyCellSx}>{student.Branch}</TableCell>
                    <TableCell sx={bodyCellSx}>{student.Category}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      {(() => {
                        const status = resolveReportStudentDisplayStatus(student);
                        const style = getReportStudentStatusStyle(status);
                        return (
                          <span
                            className="badge rounded-pill fw-semibold"
                            style={{
                              backgroundColor: style.backgroundColor,
                              color: style.color,
                              fontSize: "0.72rem",
                              padding: "0.45rem 0.65rem",
                            }}
                          >
                            {status}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      {formatCellValue(student.Max_marks)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 600,
                        color: isReportStudentAssigned(student)
                          ? "text.secondary"
                          : student.Percentage != null && student.Percentage >= 40
                            ? "success.main"
                            : student.Percentage != null
                              ? "error.main"
                              : "text.secondary",
                      }}
                      align="center"
                    >
                      {formatObtainedValue(student)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 600,
                        color: (() => {
                          if (isReportStudentAssigned(student)) {
                            return "text.secondary";
                          }
                          const finalPercent = getReportFinalScorePercent(student);
                          if (finalPercent == null) return "text.secondary";
                          return finalPercent >= 40 ? "success.main" : "error.main";
                        })(),
                      }}
                      align="center"
                    >
                      {formatFinalScoreValue(student)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 600,
                        color:
                          (student.violation_count ?? 0) > 0
                            ? "error.main"
                            : "text.secondary",
                      }}
                      align="center"
                    >
                      {student.violation_count ?? 0}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center" onClick={stopRowClick}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          justifyContent: "center",
                        }}
                      >
                        {manualReportRelease && !reportReleased && (
                          <span
                            title="Report locked for students until you release it"
                            style={{ display: "inline-flex" }}
                          >
                            <HiOutlineLockClosed
                              style={{
                                width: "22px",
                                height: "22px",
                                color: "#9e9e9e",
                              }}
                            />
                          </span>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          onClick={() =>
                            onViewStudent?.(student.ID, student.Student)
                          }
                        >
                          View
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

type ReportMetaFieldProps = {
  label: string;
  value: string;
};

const ReportMetaField: React.FC<ReportMetaFieldProps> = ({ label, value }) => (
  <div>
    <div className="small text-muted mb-1">{label}</div>
    <div
      className="border rounded bg-light px-2 py-2 small text-truncate"
      title={value}
    >
      {value || "—"}
    </div>
  </div>
);

/**
 * Report module: filter test reports, drill into results, export CSV.
 */
const TestReportContent: React.FC = () => {
  const { testName: testNameParam } = useParams<{ testName?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const routes = useTestRoutes();
  const routeTestName = decodeTestNameFromPath(testNameParam);
  const testId = searchParams.get("test_id") ?? undefined;
  const detailBatchIdFromUrl = searchParams.get("report_batch_id") ?? "";
  const isDetailRoute = !!(routeTestName && testId);

  const cardSearchQuery = useTestCardSearchQuery();
  const [reportTracks, setReportTracks] = useState<
    Awaited<ReturnType<typeof fetchAllTracksForCourses>>
  >([]);
  const [reportTracksLoading, setReportTracksLoading] = useState(false);
  const [reportCourses, setReportCourses] = useState<
    Awaited<ReturnType<typeof fetchCoursesByTrack>>
  >([]);
  const [reportCoursesLoading, setReportCoursesLoading] = useState(false);
  const [reportBatches, setReportBatches] = useState<
    Awaited<ReturnType<typeof fetchBatchesByCourse>>
  >([]);
  const [reportBatchesLoading, setReportBatchesLoading] = useState(false);
  const [reportSubjectsList, setReportSubjectsList] = useState<
    CourseSubjectOption[]
  >([]);
  const [reportSubjectsLoading, setReportSubjectsLoading] = useState(false);
  const [reportTrackId, setReportTrackId] = useState("");
  const [reportCourseId, setReportCourseId] = useState("");
  const [reportBatchId, setReportBatchId] = useState("");
  const [reportSubjectFilter, setReportSubjectFilter] = useState("");
  const [reportTestType, setReportTestType] = useState("");
  const [selectedLiveOrCompleted, setSelectedLiveOrCompleted] = useState("");

  const [reports, setReports] = useState<ReportData[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testReportView, setTestReportView] =
    useState<TestReportViewData | null>(null);
  const [detailBatchId, setDetailBatchId] = useState(detailBatchIdFromUrl);
  const [reportSlots, setReportSlots] = useState<ReportSlotOption[]>([]);
  const [reportSlotId, setReportSlotId] = useState(
    searchParams.get("report_slot_id") ?? ""
  );
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string | null>(null);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [releaseSlotIds, setReleaseSlotIds] = useState<number[]>([]);
  const [reportReleaseLoading, setReportReleaseLoading] = useState(false);
  const [showAbortModal, setShowAbortModal] = useState(false);
  const [abortAllSlots, setAbortAllSlots] = useState(false);
  const [abortSlotIds, setAbortSlotIds] = useState<number[]>([]);
  const [abortLoading, setAbortLoading] = useState(false);
  const [abortError, setAbortError] = useState<string | null>(null);
  const [examControlAlert, setExamControlAlert] = useState<{
    variant: "success" | "danger";
    message: string;
  } | null>(null);

  const handleViewStudent = (studentId: string, studentName: string) => {
    setSelectedStudentId(studentId);
    setSelectedStudentName(studentName);
    setShowStudentModal(true);
  };

  const reloadTestReport = useCallback(
    async (live = false, batchId?: string | null, slotId?: string | null) => {
      if (!testId) return;
      const resolvedBatch = batchId !== undefined ? batchId : detailBatchId;
      const resolvedSlot = slotId !== undefined ? slotId : reportSlotId;
      const data = await fetchTestReportDetail(testId, {
        live,
        batchId: resolvedBatch || null,
        slotId: resolvedSlot || null,
      });
      setTestReportView(data);
    },
    [testId, detailBatchId, reportSlotId]
  );

  const loadReportSlots = useCallback(async () => {
    if (!testId) {
      setReportSlots([]);
      return [] as ReportSlotOption[];
    }
    const res = await authService.get(`${TEST_FLOW_API}/tests/${testId}/slots`);
    const rawSlots: unknown[] = Array.isArray(res.data?.slots)
      ? res.data.slots
      : [];
    const slots = rawSlots.map(mapReportSlot);
    setReportSlots(slots);
    return slots;
  }, [testId]);

  const openReleaseModal = async () => {
    if (!isSlotReleaseReport) return;
    try {
      const slots = await loadReportSlots();
      const eligible = slots.filter((slot) => slot.can_manual_release);
      const current = eligible.find(
        (slot) => String(slot.slot_id) === String(reportSlotId)
      );
      setReleaseSlotIds(
        current ? [current.slot_id] : eligible.map((slot) => slot.slot_id)
      );
      setShowReleaseConfirm(true);
    } catch (error) {
      console.error("Failed to load slots for release:", error);
      setExamControlAlert({
        variant: "danger",
        message: "Unable to load slots for report release.",
      });
    }
  };

  const handleReleaseReport = async () => {
    if (!testId) return;
    if (releaseSlotIds.length === 0) return;
    try {
      setReportReleaseLoading(true);
      setExamControlAlert(null);
      await releaseTestReports(testId, [], true, releaseSlotIds);
      await Promise.all([reloadTestReport(), loadReportSlots()]);
      setExamControlAlert({
        variant: "success",
        message:
          releaseSlotIds.length > 1
            ? `Test report released for ${releaseSlotIds.length} slots.`
            : "Test report released. Students can now view their reports.",
      });
    } catch (error: unknown) {
      console.error("Report release failed:", error);
      const detail = (
        error as { response?: { data?: { detail?: unknown } } }
      )?.response?.data?.detail;
      const detailMessage =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail
                .map((item) =>
                  typeof item === "string"
                    ? item
                    : typeof (item as { msg?: string })?.msg === "string"
                      ? (item as { msg: string }).msg
                      : ""
                )
                .filter(Boolean)
                .join("; ")
            : "";
      setExamControlAlert({
        variant: "danger",
        message:
          detailMessage || "Failed to release test report. Please try again.",
      });
    } finally {
      setReportReleaseLoading(false);
      setShowReleaseConfirm(false);
    }
  };

  const abortableSlots = useMemo(
    () =>
      reportSlots.filter((s) => {
        const status = (s.status || "").toLowerCase();
        // Never wipe completed/aborted history slots
        return status !== "completed" && status !== "aborted";
      }),
    [reportSlots]
  );

  const openAbortModal = () => {
    setAbortAllSlots(false);
    setAbortSlotIds([]);
    setAbortError(null);
    setShowAbortModal(true);
  };

  const formatApiDetail = (detail: unknown): string => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) =>
          typeof item === "string"
            ? item
            : typeof item?.msg === "string"
              ? item.msg
              : JSON.stringify(item)
        )
        .join("; ");
    }
    if (detail != null) return String(detail);
    return "Failed to abort selected slot(s). Please try again.";
  };

  const handleAbortSlots = async () => {
    if (!testId) return;
    if (!abortAllSlots && abortSlotIds.length === 0) {
      setAbortError("Select at least one slot, or choose End for all slots.");
      return;
    }
    try {
      setAbortLoading(true);
      setAbortError(null);
      setExamControlAlert(null);
      const result = await applyExamControl(
        testId,
        "abort",
        abortAllSlots
          ? { all_slots: true }
          : { slot_ids: abortSlotIds }
      );
      await reloadTestReport();
      // Refresh slot statuses for the abort picker / filters
      try {
        const res = await authService.get(
          `${TEST_FLOW_API}/tests/${testId}/slots`
        );
        const slots = (res.data?.slots || []).map(mapReportSlot);
        setReportSlots(slots);
      } catch {
        // ignore slot refresh errors; report reload already succeeded
      }
      setShowAbortModal(false);
      setExamControlAlert({
        variant: "success",
        message:
          result.message ||
          `Aborted ${result.slots_aborted?.length ?? 0} slot(s); removed ${
            result.students_removed ?? 0
          } student(s).`,
      });
    } catch (error: unknown) {
      console.error("Abort slots failed:", error);
      const detail = (
        error as { response?: { data?: { detail?: unknown } } }
      )?.response?.data?.detail;
      setAbortError(formatApiDetail(detail));
    } finally {
      setAbortLoading(false);
    }
  };

  const displayTestStatus = testReportView?.test_status ?? "";
  const isTestOver = displayTestStatus.trim().toLowerCase() === "completed";
  const isLiveTestWindow = (() => {
    const normalized = displayTestStatus.trim().toLowerCase();
    return (
      normalized === "live" ||
      normalized === "scheduled" ||
      normalized === "paused" ||
      normalized === "upcoming"
    );
  })();

  const testStatusBadgeVariant: "aborted" | "paused" | "live" | "scheduled" | "completed" =
    (() => {
      const normalized = displayTestStatus.trim().toLowerCase();
      if (normalized === "aborted") return "aborted";
      if (normalized === "paused") return "paused";
      if (normalized === "live") return "live";
      if (normalized === "scheduled" || normalized === "upcoming") return "scheduled";
      return "completed";
    })();

  const isImmediateAutoReleaseReport = isImmediateAutoReleaseTestType(
    testReportView?.test_type ?? ""
  );
  const isFinalLikeReport = isFinalLikeTestType(
    testReportView?.test_type ?? ""
  );
  const isSlotReleaseReport = isSlotReleaseTestType(
    testReportView?.test_type ?? ""
  );
  const slotScopedAutoRelease = (() => {
    if (!isSlotReleaseReport || reportSlots.length === 0) return null;
    if (reportSlotId) {
      const selected = reportSlots.find(
        (slot) => String(slot.slot_id) === String(reportSlotId)
      );
      return selected ? Boolean(selected.auto_release_test_report) : null;
    }
    return reportSlots.every((slot) => Boolean(slot.auto_release_test_report));
  })();
  const autoReleaseEnabled = Boolean(
    isImmediateAutoReleaseReport ||
      (isSlotReleaseReport &&
        (testReportView?.auto_release_test_report || slotScopedAutoRelease))
  );
  const manualSlots = isSlotReleaseReport
    ? reportSlots.filter((slot) => !slot.auto_release_test_report)
    : [];
  const selectedReportSlot = reportSlots.find(
    (slot) => String(slot.slot_id) === String(reportSlotId)
  );
  const manualReportRelease = isSlotReleaseReport && !autoReleaseEnabled;
  const reportReleased = !isSlotReleaseReport
    ? Boolean(testReportView?.report_released)
    : reportSlotId
      ? Boolean(selectedReportSlot?.report_released)
      : manualSlots.length > 0
        ? manualSlots.every((slot) => slot.report_released)
        : Boolean(testReportView?.report_released);
  const canAbortSlots =
    isFinalLikeReport && abortableSlots.length > 0;

  useEffect(() => {
    if (routeTestName && !testId) {
      navigate(routes.reports, { replace: true });
    }
  }, [routeTestName, testId, navigate, routes.reports]);

  useEffect(() => {
    setDetailBatchId(detailBatchIdFromUrl);
  }, [detailBatchIdFromUrl, testId]);

  useEffect(() => {
    setReportSlotId(searchParams.get("report_slot_id") ?? "");
  }, [searchParams, testId]);

  useEffect(() => {
    if (!isDetailRoute || !testId) {
      setTestReportView(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setDetailLoading(true);
        const data = await fetchTestReportDetail(testId, {
          live: true,
          batchId: detailBatchId || null,
          slotId: reportSlotId || null,
        });
        if (cancelled) return;
        setTestReportView(data);
      } catch (error) {
        console.error("Error fetching test report:", error);
        if (!cancelled) navigate(routes.reports, { replace: true });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isDetailRoute,
    testId,
    detailBatchId,
    reportSlotId,
    navigate,
    routes.reports,
  ]);

  useEffect(() => {
    if (!isDetailRoute || !testId || !isLiveTestWindow) return undefined;

    const timer = window.setInterval(() => {
      void fetchTestReportDetail(testId, {
        live: true,
        batchId: detailBatchId || null,
        slotId: reportSlotId || null,
      })
        .then((data) => {
          setTestReportView(data);
        })
        .catch((error) => console.error("Live report refresh failed:", error));
    }, 5000);

    return () => window.clearInterval(timer);
  }, [isDetailRoute, testId, isLiveTestWindow, detailBatchId, reportSlotId]);

  useEffect(() => {
    if (!checkAuthAndRedirect()) return;
    let cancelled = false;
    (async () => {
      setReportTracksLoading(true);
      try {
        const tracks = await fetchAllTracksForCourses();
        if (!cancelled) setReportTracks(tracks);
      } catch (err) {
        console.error("Failed to fetch tracks for report:", err);
        if (!cancelled) setReportTracks([]);
      } finally {
        if (!cancelled) setReportTracksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!testId) {
      setReportSlots([]);
      setReportSlotId("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const slots = await loadReportSlots();
        if (cancelled) return;
        const urlSlot = searchParams.get("report_slot_id") ?? "";
        const urlSlotValid = slots.some(
          (slot) => String(slot.slot_id) === urlSlot
        );
        if (urlSlot && urlSlotValid) {
          setReportSlotId(urlSlot);
        } else {
          setReportSlotId("");
          if (urlSlot) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("report_slot_id");
                return next;
              },
              { replace: true }
            );
          }
        }
      } catch (err) {
        console.error("Failed to load report slots:", err);
        if (!cancelled) {
          setReportSlots([]);
          setReportSlotId("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, loadReportSlots]);

  useEffect(() => {
    if (!reportTrackId) {
      setReportCourses([]);
      setReportCourseId("");
      return;
    }
    let cancelled = false;
    (async () => {
      setReportCoursesLoading(true);
      try {
        const courses = await fetchCoursesByTrack(reportTrackId);
        if (!cancelled) {
          setReportCourses(courses);
          setReportCourseId("");
          setReportBatchId("");
          setReportSubjectFilter("");
          setReportBatches([]);
          setReportSubjectsList([]);
        }
      } catch (err) {
        console.error("Failed to fetch courses for report:", err);
        if (!cancelled) {
          setReportCourses([]);
          setReportCourseId("");
        }
      } finally {
        if (!cancelled) setReportCoursesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportTrackId]);

  useEffect(() => {
    if (!reportCourseId) {
      setReportBatches([]);
      setReportBatchId("");
      return;
    }
    let cancelled = false;
    (async () => {
      setReportBatchesLoading(true);
      try {
        const batches = await fetchBatchesByCourse(reportCourseId);
        if (!cancelled) {
          setReportBatches(batches);
          setReportBatchId("");
        }
      } catch (err) {
        console.error("Failed to fetch batches for report:", err);
        if (!cancelled) {
          setReportBatches([]);
          setReportBatchId("");
        }
      } finally {
        if (!cancelled) setReportBatchesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportCourseId]);

  useEffect(() => {
    if (!reportCourseId) {
      setReportSubjectsList([]);
      setReportSubjectFilter("");
      return;
    }
    let cancelled = false;
    (async () => {
      setReportSubjectsLoading(true);
      try {
        const subjects = await fetchSubjectsOfCourses([reportCourseId]);
        if (!cancelled) {
          setReportSubjectsList(subjects);
          setReportSubjectFilter("");
        }
      } catch (err) {
        console.error("Failed to fetch subjects for report:", err);
        if (!cancelled) {
          setReportSubjectsList([]);
          setReportSubjectFilter("");
        }
      } finally {
        if (!cancelled) setReportSubjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportCourseId]);

  useEffect(() => {
    const load = async () => {
      if (!reportTrackId || !reportCourseId) {
        setReports([]);
        return;
      }
      setReportLoading(true);
      try {
        const list = await fetchReportsList({
          track: reportTrackId,
          subject: reportSubjectFilter,
          course: reportCourseId,
          batch: reportBatchId,
          liveOrCompleted: selectedLiveOrCompleted,
          testType: reportTestType,
        });
        setReports(list);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setReports([]);
      } finally {
        setReportLoading(false);
      }
    };
    load();
  }, [
    reportTrackId,
    reportCourseId,
    reportBatchId,
    reportSubjectFilter,
    reportTestType,
    selectedLiveOrCompleted,
  ]);

  const filteredReports = reports
    .filter((report) => {
      if (!selectedLiveOrCompleted) return true;
      return (
        String(report.status || "").trim().toLowerCase() ===
        selectedLiveOrCompleted.trim().toLowerCase()
      );
    })
    .filter((report) =>
      matchesVisibleCardSearch(
        getVisibleReportSearchText(report),
        cardSearchQuery
      )
    );

  const filteredReport = filterReportDetailRows(
    testReportView?.report,
    isDetailRoute ? cardSearchQuery : ""
  );

  const reportStats = useMemo(
    () =>
      computeReportDetailStats(
        testReportView?.report,
        testReportView?.test_end_time,
        isTestOver
      ),
    [testReportView?.report, testReportView?.test_end_time, isTestOver]
  );
  const handleTestReportView = (report: ReportData) => {
    navigate(routes.reportDetail(report.title, report.test_id));
  };

  return (
    <div
      className="position-relative"
      style={isDetailRoute ? { minHeight: "calc(100vh - 180px)" } : undefined}
    >
      {isDetailRoute && detailLoading && !testReportView && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {detailLoading && testReportView && (
        <div style={loadingOverlayStyle}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {!isDetailRoute && (
        <div className="d-flex rounded-1 justify-content-start py-2 gap-2">
          <div
            className="col-3 filterSection flex-shrink-0"
            style={{
              height: "calc(100vh - 150px)",
              overflowY: "auto",
              minWidth: "200px",
            }}
          >
            <div
              className="border m-0 border-light p-3 px-4"
              style={{ boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
            >
              <div className="d-flex flex-column gap-3">
                <TestFilterSelect
                  label="Track"
                  value={reportTrackId}
                  onChange={setReportTrackId}
                  disabled={reportTracksLoading}
                  placeholder="Select Track"
                  options={reportTracks.map((t) => ({
                    value: t.track_id,
                    label: t.track_name,
                  }))}
                  fullWidth
                />
                <TestFilterSelect
                  label="Course"
                  value={reportCourseId}
                  onChange={setReportCourseId}
                  disabled={!reportTrackId || reportCoursesLoading}
                  placeholder="Select Course"
                  options={reportCourses.map((c) => ({
                    value: c.course_id,
                    label: c.course_name,
                  }))}
                  fullWidth
                />
                <TestFilterSelect
                  label="Batch"
                  value={reportBatchId}
                  onChange={setReportBatchId}
                  disabled={
                    !reportCourseId ||
                    reportBatchesLoading ||
                    reportBatches.length === 0
                  }
                  placeholder="Select Batch"
                  options={reportBatches.map((b) => ({
                    value: b.batch_id,
                    label: b.batch_name,
                  }))}
                  fullWidth
                />
                <TestFilterSelect
                  label="Subject"
                  value={reportSubjectFilter}
                  onChange={setReportSubjectFilter}
                  disabled={
                    !reportCourseId ||
                    reportSubjectsLoading ||
                    reportSubjectsList.length === 0
                  }
                  placeholder="Select Subject"
                  options={reportSubjectsList.map((subject) => ({
                    value: subject.subject_id,
                    label: subject.subject_name,
                  }))}
                  fullWidth
                />
                <TestFilterSelect
                  label="Test Type"
                  value={reportTestType}
                  onChange={setReportTestType}
                  placeholder="Select Test Type"
                  options={TEST_TYPES.map((type) => ({
                    value: type,
                    label: type,
                  }))}
                  fullWidth
                />
                <TestFilterSelect
                  label="Status"
                  value={selectedLiveOrCompleted}
                  onChange={setSelectedLiveOrCompleted}
                  placeholder="All statuses"
                  options={[
                    { value: "Live", label: "Live" },
                    { value: "Scheduled", label: "Scheduled" },
                    { value: "Completed", label: "Completed" },
                  ]}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div
            className="col filteredDataDisplaySection flex-grow-1 p-2"
            style={{
              height: "calc(100vh - 150px)",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {!(reportTrackId && reportCourseId) ? (
              <div
                className="d-flex justify-content-center align-items-center text-secondary"
                style={{ height: "100%" }}
              >
                <p className="text-center px-3 mb-0">
                  Please select a track and course to load test reports.
                  Optionally narrow by batch and subject.
                </p>
              </div>
            ) : reportLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : filteredReports.length > 0 ? (
              filteredReports.map((report) => {
                const cardDate = getReportCardDate(report);
                const timeRange = formatReportTimeRange(report);
                return (
                  <div
                    key={report.test_id}
                    className="mb-2 ms-2 p-2 bg-white rounded border border-light shadow-sm"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleTestReportView(report)}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-2 pb-1 border-bottom">
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold mb-0 lh-sm d-flex align-items-center flex-wrap gap-2">
                          <span>{report.title || "Test Title"}</span>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-1 text-secondary">
                          <span className="d-inline-flex align-items-center">
                            <VscListFlat className="me-1" />
                            {report.subject || "N/A"}
                          </span>
                          <span className="d-inline-flex align-items-center">
                            <BsClock className="me-1" style={metaIconStyle} />
                            {parseInt(report.duration)}{" "}
                            {parseInt(report.duration) === 1
                              ? "Minute"
                              : "Minutes"}
                          </span>
                          <span className="d-inline-flex align-items-center">
                            <GiStarShuriken className="me-1" />
                            {report.marks} Marks
                          </span>
                          {report.test_type && (
                            <span className="d-inline-flex align-items-center">
                              <MdQuiz className="me-1" />
                              {report.test_type}
                            </span>
                          )}
                          <span className="d-inline-flex align-items-center">
                            <TiGroupOutline className="me-1" size={18} />
                            {report.invited} invited
                          </span>
                        </div>
                      </div>
                      {(cardDate || timeRange) && (
                        <TestCardSchedule
                          dateLabel={cardDate}
                          timeLabel={timeRange}
                        />
                      )}
                    </div>
                    <div className="pt-1">
                      {report.batch && (
                        <div className="fs-6 mb-1">
                          <span className="fw-semibold">Batch:</span>{" "}
                          {report.batch}
                        </div>
                      )}
                      <div>
                        <span className="fw-semibold">Description</span>
                        <div className="text-dark lh-sm mt-1 text-break">
                          {report.description || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : reports.length > 0 && cardSearchQuery ? (
              <p className="text-muted ms-3 py-3 text-center">
                No tests match your search.
              </p>
            ) : (
              <p className="text-muted ms-3 py-3 text-center">
                No test reports found for the selected filters.
              </p>
            )}
          </div>
        </div>
      )}

      {isDetailRoute && testReportView && (
        <div className="rounded p-2" style={{ backgroundColor: "#f3f4f6" }}>
          {examControlAlert && (
            <Alert
              variant={examControlAlert.variant}
              dismissible
              onClose={() => setExamControlAlert(null)}
              className="mb-2"
            >
              {examControlAlert.message}
            </Alert>
          )}
          <div className="d-flex justify-content-end align-items-center mb-2 gap-2 flex-wrap">
            {canAbortSlots && (
              <Button
                variant="outline-danger"
                className="btn btn-sm"
                size="sm"
                disabled={abortLoading}
                onClick={openAbortModal}
              >
                Abort Test
              </Button>
            )}
            {autoReleaseEnabled ? (
              <span
                className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-3 py-2"
                style={{ fontSize: "0.8rem" }}
              >
                {isImmediateAutoReleaseReport
                  ? "Auto-release on student completion"
                  : reportReleased
                    ? "Report auto-released to students"
                    : "Auto-release after test end"}
              </span>
            ) : (
              manualReportRelease &&
              (reportReleased ? (
                <span
                  className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-3 py-2"
                  style={{ fontSize: "0.8rem" }}
                >
                  Report Released to Students
                </span>
              ) : (
                <Button
                  variant="primary"
                  className="btn btn-sm"
                  size="sm"
                  disabled={reportReleaseLoading}
                  onClick={() => void openReleaseModal()}
                >
                  {reportReleaseLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Releasing...
                    </>
                  ) : (
                    "Release Report to Students"
                  )}
                </Button>
              ))
            )}
            <Button
              variant="outline-primary"
              className="btn btn-sm"
              size="sm"
              onClick={() =>
                downloadTestReportCsv(filteredReport, testReportView)
              }
            >
              Download Report
            </Button>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <div className="card shadow-sm border-0 bg-white h-100">
                <div className="card-body d-flex flex-column gap-3">
                  <Row className="g-3">
                    <Col xs={12} md={reportSlots.length > 1 ? 3 : 4}>
                      <ReportMetaField
                        label="Course"
                        value={testReportView.course_name}
                      />
                    </Col>
                    {reportSlots.length > 1 && (
                      <Col xs={12} md={3}>
                        <div>
                          <div className="small text-muted mb-1">Slot</div>
                          <Form.Select
                            size="sm"
                            value={reportSlotId}
                            onChange={(e) => {
                              const nextSlotId = e.target.value;
                              setReportSlotId(nextSlotId);
                              // Changing slot resets batch filter to All
                              setDetailBatchId("");
                              setSearchParams(
                                (prev) => {
                                  const next = new URLSearchParams(prev);
                                  if (testId) next.set("test_id", testId);
                                  if (nextSlotId) {
                                    next.set("report_slot_id", nextSlotId);
                                  } else {
                                    next.delete("report_slot_id");
                                  }
                                  next.delete("report_batch_id");
                                  return next;
                                },
                                { replace: true }
                              );
                            }}
                            className="bg-light"
                          >
                            <option value="">All</option>
                            {reportSlots.map((slot) => (
                              <option
                                key={slot.slot_id}
                                value={String(slot.slot_id)}
                              >
                                {slot.label}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Col>
                    )}
                    <Col xs={12} md={reportSlots.length > 1 ? 3 : 4}>
                      <div>
                        <div className="small text-muted mb-1">Batch</div>
                        {(testReportView.assigned_batches?.length ?? 0) > 0 &&
                        ((testReportView.assigned_batches?.length ?? 0) > 1 ||
                          reportSlots.length > 1) ? (
                          <Form.Select
                            size="sm"
                            value={detailBatchId}
                            onChange={(e) => {
                              const nextBatchId = e.target.value;
                              setDetailBatchId(nextBatchId);
                              setSearchParams(
                                (prev) => {
                                  const next = new URLSearchParams(prev);
                                  if (testId) next.set("test_id", testId);
                                  if (nextBatchId) {
                                    next.set("report_batch_id", nextBatchId);
                                  } else {
                                    next.delete("report_batch_id");
                                  }
                                  return next;
                                },
                                { replace: true }
                              );
                            }}
                            className="bg-light"
                          >
                            <option value="">All</option>
                            {(testReportView.assigned_batches ?? []).map((batch) => (
                              <option key={batch.batch_id} value={batch.batch_id}>
                                {batch.batch_name || batch.batch_id}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <div
                            className="border rounded bg-light px-2 py-2 small text-truncate"
                            title={testReportView.batch_name}
                          >
                            {testReportView.batch_name || "—"}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col xs={12} md={reportSlots.length > 1 ? 3 : 4}>
                      <ReportMetaField
                        label="Test Name"
                        value={testReportView.test_name}
                      />
                    </Col>
                  </Row>

                  <Row className="g-3 align-items-end">
                    <Col xs={12} md={4}>
                      <div>
                        <div className="small text-muted mb-1">Test Status</div>
                        <span style={getTestStatusBadgeStyle(testStatusBadgeVariant)}>
                          {displayTestStatus || "—"}
                        </span>
                      </div>
                    </Col>
                    <Col xs={12} md={5}>
                      <ReportMetaField
                        label="Date - Time"
                        value={`${testReportView.test_start_time} - ${testReportView.test_end_time}`}
                      />
                    </Col>
                    <Col xs={12} md={3}>
                      <ReportMetaField
                        label="Duration"
                        value={testReportView.duration}
                      />
                    </Col>
                  </Row>

                  <div className="border-top pt-3 mt-auto">
                    <div className="row text-center g-3">
                      <div className="col">
                        <div className="text-muted small text-uppercase">Total Students</div>
                        <div className="fs-4 fw-bold text-primary">
                          {reportStats.totalStudents}
                        </div>
                      </div>
                      <div className="col">
                        <div className="text-muted small text-uppercase">Appeared</div>
                        <div className="fs-4 fw-bold" style={{ color: "#6f42c1" }}>
                          {reportStats.appeared}
                        </div>
                      </div>
                      <div className="col">
                        <div className="text-muted small text-uppercase">Cleared</div>
                        <div className="fs-4 fw-bold text-success">{reportStats.cleared}</div>
                      </div>
                      <div className="col">
                        <div className="text-muted small text-uppercase">Failed</div>
                        <div className="fs-4 fw-bold text-danger">{reportStats.failed}</div>
                      </div>
                      <div className="col">
                        <div className="text-muted small text-uppercase">Avg Score</div>
                        <div className="fs-4 fw-bold text-dark">
                          {reportStats.avgScore.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              {testId && (
                <SecurityLogsPanel
                  testId={testId}
                  testStatus={displayTestStatus}
                  studentIds={(testReportView?.report ?? [])
                    .map((row) => row.ID)
                    .filter(Boolean)}
                  onViolationsUpdated={() => reloadTestReport(true)}
                />
              )}
            </div>
          </div>

          <div className="card shadow-sm border-0 bg-white">
            <ReportStudentTable
              rows={filteredReport}
              onViewStudent={handleViewStudent}
              manualReportRelease={manualReportRelease}
              reportReleased={reportReleased}
              containerStyle={{
                minHeight: "250px",
                height: "calc(100vh - 500px)",
              }}
            />
          </div>
        </div>
      )}

      <Modal
        show={showReleaseConfirm}
        onHide={() => !reportReleaseLoading && setShowReleaseConfirm(false)}
        centered
        backdrop="static"
        className="test-flow-modal"
      >
        <Modal.Header closeButton={!reportReleaseLoading}>
          <Modal.Title>Release Report by Slot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportSlots.filter((slot) => !slot.auto_release_test_report).length ===
          0 ? (
            <p className="text-muted small mb-0">
              No manual-release slots on this test.
            </p>
          ) : (
            reportSlots
              .filter((slot) => !slot.auto_release_test_report)
              .map((slot) => {
                const selected = releaseSlotIds.includes(slot.slot_id);
                const disabled =
                  reportReleaseLoading ||
                  slot.report_released ||
                  !slot.can_manual_release;
                return (
                  <div key={slot.slot_id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`release-slot-${slot.slot_id}`}
                      disabled={disabled}
                      checked={selected || slot.report_released}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setReleaseSlotIds((prev) =>
                          checked
                            ? [...prev, slot.slot_id]
                            : prev.filter((id) => id !== slot.slot_id)
                        );
                      }}
                      label={
                        <span>
                          {slot.label || `Slot ${slot.slot_id}`}
                          <span className="text-muted">
                            {` · Attendance: ${
                              slot.consider_attendance ? "Yes" : "No"
                            }`}
                          </span>
                        </span>
                      }
                    />
                    <div className="ms-4 small text-muted">
                      {slot.report_released
                        ? "Already released"
                        : slot.can_manual_release
                          ? slot.manual_release_allow_reason ||
                            "Eligible to release"
                          : slot.manual_release_block_reason ||
                            "Not eligible yet"}
                    </div>
                  </div>
                );
              })
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={reportReleaseLoading}
            onClick={() => setShowReleaseConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={reportReleaseLoading || releaseSlotIds.length === 0}
            onClick={() => void handleReleaseReport()}
          >
            {reportReleaseLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Releasing...
              </>
            ) : (
              "Release Report"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAbortModal}
        onHide={() => !abortLoading && setShowAbortModal(false)}
        centered
        backdrop="static"
        className="test-flow-modal"
      >
        <Modal.Header closeButton={!abortLoading}>
          <Modal.Title>Abort Final Test Slot(s)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {abortError && (
            <Alert
              variant="danger"
              className="mb-3"
              dismissible
              onClose={() => setAbortError(null)}
            >
              {abortError}
            </Alert>
          )}
          <p className="mb-3 text-muted small">
            Ending a slot removes every assigned student from that slot. Students
            currently in the test will be exited. Aborted tests will not appear
            on the student test list.
          </p>
          <Form.Check
            type="checkbox"
            id="abort-all-slots"
            className="mb-3"
            label="End for all slots"
            checked={abortAllSlots}
            disabled={abortLoading}
            onChange={(e) => {
              const checked = e.target.checked;
              setAbortAllSlots(checked);
              if (checked) setAbortSlotIds([]);
            }}
          />
          <div className={abortAllSlots ? "opacity-50" : ""}>
            <div className="fw-semibold mb-2">End for selected slot(s)</div>
            {abortableSlots.length === 0 ? (
              <p className="text-muted small mb-0">No active slots to abort.</p>
            ) : (
              abortableSlots.map((slot) => (
                <Form.Check
                  key={slot.slot_id}
                  type="checkbox"
                  id={`abort-slot-${slot.slot_id}`}
                  className="mb-1"
                  label={`${slot.label}${
                    slot.status ? ` (${slot.status})` : ""
                  }`}
                  checked={abortSlotIds.includes(slot.slot_id)}
                  disabled={abortLoading || abortAllSlots}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAbortSlotIds((prev) =>
                      checked
                        ? [...prev, slot.slot_id]
                        : prev.filter((id) => id !== slot.slot_id)
                    );
                  }}
                />
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={abortLoading}
            onClick={() => setShowAbortModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              abortLoading ||
              (!abortAllSlots && abortSlotIds.length === 0)
            }
            onClick={() => void handleAbortSlots()}
          >
            {abortLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Aborting...
              </>
            ) : (
              "Abort"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStudentModal}
        onHide={() => setShowStudentModal(false)}
        size="xl"
        centered
        scrollable
        backdrop="static"
        keyboard={false}
        className="test-flow-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedStudentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white overflow-auto">
          {selectedStudentId && testId && (
            <StudentReport
              studentId={selectedStudentId}
              testId={testId}
              testType={testReportView?.test_type ?? ""}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TestReportContent;
